/**
 * Client session seam for the AVOWAL portal.
 *
 * The portal is client-only (D3). Production auth is AWS Cognito (JWT). Two
 * paths coexist, selected at runtime by whether a Cognito pool is configured
 * (VITE_COGNITO_* — see lib/cognito.ts):
 *
 *   - Cognito configured: `signInWithPassword` performs real SRP auth, the
 *     id token is cached and `useSession` reflects the live Cognito session.
 *     API calls attach `Authorization: Bearer <idToken>` (see authHeader).
 *   - Cognito NOT configured AND import.meta.env.DEV: the DEV-only demo session
 *     (sessionStorage) lets the surface be reviewed without a prod bypass.
 *     `import.meta.env.DEV` is false in `vite build`, so the demo path is
 *     compiled out of the public bundle.
 *
 * `getSession` stays synchronous so `useSession`'s initial render is correct;
 * the live Cognito token is mirrored into module state + sessionStorage on
 * sign-in and rehydrated on mount via `refreshSession`.
 */
import { useEffect, useState } from 'react';
import {
  cognitoConfig,
  cognitoSignIn,
  cognitoSignOut,
  currentCognitoTokens,
  type CognitoTokens,
} from './cognito';
import type { TenantConfig } from './tenant';

export interface Session {
  clientName: string;
  // matterIds land with the matters API; token lives in module state (authHeader).
}

const DEMO_KEY = 'avowal.portal.demo';
const TOKEN_KEY = 'avowal.portal.token';
const SESSION_EVENT = 'avowal:session';

interface StoredAuth {
  idToken: string;
  accessToken: string;
  expiresAt: number;
  email: string;
}

// Live token mirror. Kept in module state (and sessionStorage) so getSession()
// and authHeader() are synchronous.
let authState: StoredAuth | null = readStoredAuth();

function readStoredAuth(): StoredAuth | null {
  try {
    const raw = sessionStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredAuth>;
    if (
      typeof parsed.idToken === 'string' &&
      typeof parsed.accessToken === 'string' &&
      typeof parsed.expiresAt === 'number' &&
      typeof parsed.email === 'string'
    ) {
      return {
        idToken: parsed.idToken,
        accessToken: parsed.accessToken,
        expiresAt: parsed.expiresAt,
        email: parsed.email,
      };
    }
  } catch {
    /* ignore malformed storage */
  }
  return null;
}

function setAuth(tokens: CognitoTokens | null): void {
  if (tokens) {
    authState = {
      idToken: tokens.idToken,
      accessToken: tokens.accessToken,
      expiresAt: tokens.expiresAt,
      email: tokens.email,
    };
    sessionStorage.setItem(TOKEN_KEY, JSON.stringify(authState));
  } else {
    authState = null;
    sessionStorage.removeItem(TOKEN_KEY);
  }
  window.dispatchEvent(new Event(SESSION_EVENT));
}

function tokenIsLive(a: StoredAuth | null): a is StoredAuth {
  // 30s skew so a near-expired token isn't treated as valid.
  return a !== null && a.expiresAt * 1000 > Date.now() + 30_000;
}

function clientNameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? email;
  return local || 'Client';
}

export function getSession(): Session | null {
  if (tokenIsLive(authState)) {
    return { clientName: clientNameFromEmail(authState.email) };
  }
  if (!cognitoConfig() && import.meta.env.DEV && sessionStorage.getItem(DEMO_KEY)) {
    return { clientName: 'Demo Client' };
  }
  return null;
}

/**
 * Authorization header for API calls. Empty object when there is no live
 * Cognito token — in that case the API relies on its DEV_AUTH bypass.
 */
export function authHeader(): Record<string, string> {
  if (tokenIsLive(authState)) {
    return { Authorization: `Bearer ${authState.idToken}` };
  }
  return {};
}

/** Real Cognito sign-in. Establishes the portal session on success. */
export async function signInWithPassword(email: string, password: string): Promise<void> {
  const tokens = await cognitoSignIn(email, password);
  setAuth(tokens);
}

/** DEV-only demo session (no Cognito). Compiled out of production builds. */
export function startDemoSession(): void {
  sessionStorage.setItem(DEMO_KEY, '1');
  window.dispatchEvent(new Event(SESSION_EVENT));
}

export function signOut(): void {
  cognitoSignOut();
  setAuth(null);
  sessionStorage.removeItem(DEMO_KEY);
  window.dispatchEvent(new Event(SESSION_EVENT));
}

/**
 * Rehydrate the live Cognito session (e.g. on mount or page reload). Pulls a
 * fresh/refreshed token from the SDK and mirrors it into module state. No-op
 * when Cognito is not configured.
 *
 *   - 'tokens': adopt the refreshed token.
 *   - 'none':   the SDK has no signed-in user — clear the mirror (signed out).
 *   - 'error':  transient refresh failure — keep any still-live mirrored token
 *               so a network blip on reload doesn't bounce the user to the gate.
 */
export async function refreshSession(): Promise<void> {
  if (!cognitoConfig()) return;
  const result = await currentCognitoTokens();
  if (result.status === 'tokens') {
    setAuth(result.tokens);
  } else if (result.status === 'none') {
    setAuth(null);
  }
  // 'error': leave authState untouched (preserve live mirror).
}

export type UserRole = 'client' | 'attorney' | 'admin' | 'system';
export interface Me {
  id: string; email: string; role: UserRole;
  firstName: string | null; lastName: string | null; emailVerified?: boolean;
  /** White-label tenant config, when the API supplies it (see lib/tenant). */
  tenant?: TenantConfig;
}
let _meCache: Me | null = null;
/** Fetch the current user (incl. role) from the API. Cached for the session. */
export async function fetchMe(): Promise<Me | null> {
  try {
    const res = await fetch('/api/auth/me', { headers: { Accept: 'application/json', ...authHeader() } });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body?.ok === false || !body?.user) return null;
    _meCache = body.user as Me;
    return _meCache;
  } catch { return null; }
}
/** Role-aware current user. `loading` is true until the first fetch resolves. */
export function useMe(): { me: Me | null; loading: boolean } {
  const [me, setMe] = useState<Me | null>(_meCache);
  const [loading, setLoading] = useState(!_meCache);
  useEffect(() => {
    let live = true;
    fetchMe().then((m) => { if (live) { setMe(m); setLoading(false); } });
    return () => { live = false; };
  }, []);
  return { me, loading };
}

export function useSession(): Session | null {
  const [session, setSession] = useState<Session | null>(getSession);
  useEffect(() => {
    const sync = () => setSession(getSession());
    window.addEventListener(SESSION_EVENT, sync);
    window.addEventListener('storage', sync);
    // Rehydrate a real Cognito session on mount (handles reloads / token refresh).
    void refreshSession();
    return () => {
      window.removeEventListener(SESSION_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return session;
}
